import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function DeliveryFields({ value = {}, onChange }) {
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const [notice, setNotice] = useState("");
  const cep = value.cep || "";

  useEffect(() => {
    setNotice("");
    if (!/^[0-9]{8}$/.test(cep)) return;
    let ignore = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    setNotice("Consultando CEP…");
    async function lookup() {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
          signal: controller.signal, credentials: "omit", referrerPolicy: "no-referrer",
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (ignore) return;
        if (data.erro) {
          setNotice("CEP não encontrado. Confira o CEP específico da rua nos Correios ou preencha o endereço manualmente.");
          return;
        }
        if (typeof data.cep !== "string" || data.cep.replace(/-/g, "") !== cep ||
            typeof data.localidade !== "string" || !data.localidade.trim() || data.localidade.length > 100 ||
            typeof data.uf !== "string" || !/^[A-Z]{2}$/.test(data.uf) ||
            typeof data.logradouro !== "string" || data.logradouro.length > 200 ||
            typeof data.bairro !== "string" || data.bairro.length > 100) throw new Error();
        const current = latest.current.value;
        const normalize = text => text.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
        if ((current.municipio && normalize(current.municipio) !== normalize(data.localidade)) ||
            (current.uf && normalize(current.uf) !== data.uf)) {
          setNotice(`O CEP pertence a ${data.localidade}/${data.uf}, diferente da cidade/UF informada. Confira antes de salvar; nenhum campo foi alterado.`);
          return;
        }
        const filled = { ...current };
        for (const [field, source] of [["municipio", "localidade"], ["uf", "uf"], ["bairro", "bairro"], ["logradouro", "logradouro"]]) {
          if (!filled[field]?.trim() && data[source]) filled[field] = data[source];
        }
        latest.current.onChange(filled);
        setNotice(`CEP: ${[data.logradouro, data.complemento, data.bairro, `${data.localidade}/${data.uf}`].filter(Boolean).join(" · ")}. ${data.logradouro ? "Completei apenas campos vazios. Confira rua e bairro; informe número e complemento." : "CEP geral: confirme se o endereço fica nesse município e complete rua, bairro e número."}`);
      } catch {
        if (!ignore) setNotice("Não foi possível consultar o CEP. Você pode preencher o endereço manualmente.");
      } finally {
        clearTimeout(timeout);
      }
    }
    lookup();
    return () => { ignore = true; controller.abort(); clearTimeout(timeout); };
  }, [cep]);

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Endereço para nota de entrega</legend>
      <p className="text-xs text-muted-foreground">Complete para preparar a emissão da NFC-e. O pedido pode ser salvo para conferência posterior.</p>
      <p className="text-xs text-muted-foreground">Informe o CEP específico da rua para sugerir o município e completar campos vazios. Não definimos cidade pelo nome do bairro.</p>
      {notice && <p role="status" className="text-xs">{notice}</p>}
      <div className="grid grid-cols-2 gap-3">
        {[["cep", "CEP (8 dígitos)"], ["municipio", "Cidade"], ["uf", "UF"], ["bairro", "Bairro"], ["logradouro", "Rua/avenida"], ["numero", "Número"], ["complemento", "Complemento"]].map(([key, label]) => (
          <div key={key}>
            <Label htmlFor={`delivery-${key}`} className="text-xs">{label}</Label>
            <Input id={`delivery-${key}`} value={value[key] || ""} maxLength={key === "uf" ? 2 : key === "cep" ? 9 : 200}
              onChange={e => onChange({ ...value, [key]: key === "cep" ? e.target.value.replace(/\D/g, "").slice(0, 8) : key === "uf" ? e.target.value.toUpperCase() : e.target.value })} />
          </div>
        ))}
      </div>
    </fieldset>
  );
}
